/**
 * InfoTablePage – Typ B: Dynamische Tabelle für ein Info-Modul.
 * Mitarbeiter können Zeilen hinzufügen, Admin kann alles löschen + CSV exportieren.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Plus, Trash2, Download, FileDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InfoTablePage() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [rowData, setRowData] = useState({});
  const [saving, setSaving] = useState(false);
  const isAdmin = ['admin', 'superadmin', 'stationsleitung'].includes(user?.role);
  const canAdd = isAdmin || (module?.employee_can_add !== false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchModule = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/info/modules`, { headers });
      const found = (res.data || []).find(m => m.id === moduleId);
      if (found) setModule(found);
    } catch { toast.error('Modul nicht gefunden'); }
  }, [moduleId]);

  const fetchEntries = useCallback(async () => {
    try {
      const params = selectedLocation ? { location_id: selectedLocation } : {};
      const res = await axios.get(`${API}/api/info/${moduleId}/entries`, { headers, params });
      setEntries(res.data || []);
    } catch { toast.error('Fehler beim Laden'); }
  }, [moduleId, selectedLocation]);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/user/locations`, { headers });
      const locs = res.data || [];
      setLocations(locs);
      if (locs.length === 1) setSelectedLocation(locs[0].id);
    } catch {}
  }, []);

  useEffect(() => {
    Promise.all([fetchModule(), fetchLocations()]).then(() => setLoading(false));
  }, []);

  useEffect(() => { if (!loading) fetchEntries(); }, [loading, selectedLocation]);

  const openAdd = () => {
    const initial = {};
    (module?.table_schema || []).forEach(c => { initial[c.key] = ''; });
    setRowData(initial);
    setShowAddDialog(true);
  };

  const handleAddRow = async () => {
    if (!selectedLocation && user?.role !== 'admin') {
      toast.error('Bitte zuerst einen Standort auswählen');
      return;
    }
    setSaving(true);
    try {
      const loc = selectedLocation || (user?.location_ids?.[0]);
      await axios.post(`${API}/api/info/${moduleId}/entries`, {
        location_id: loc,
        row_data: rowData,
      }, { headers });
      toast.success('Eintrag hinzugefügt');
      setShowAddDialog(false);
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Eintrag löschen?')) return;
    try {
      await axios.delete(`${API}/api/info/${moduleId}/entries/${entryId}`, { headers });
      toast.success('Gelöscht');
      fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleExportCsv = async () => {
    try {
      const params = selectedLocation ? `?location_id=${selectedLocation}` : '';
      const res = await axios.get(
        `${API}/api/info/${moduleId}/export-csv${params}`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${module?.name || 'export'}_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV heruntergeladen');
    } catch { toast.error('Fehler beim Export'); }
  };

  const locName = (id) => locations.find(l => l.id === id)?.name || id;

  const renderCell = (col, value) => {
    if (!value) return <span className="text-muted-foreground">–</span>;
    if (col.type === 'date') {
      try { return new Date(value).toLocaleDateString('de-DE'); }
      catch { return value; }
    }
    return value;
  };

  const schema = module?.table_schema || [];

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    </DashboardLayout>
  );

  if (!module) return (
    <DashboardLayout>
      <div className="p-6 text-center text-muted-foreground">Modul nicht gefunden</div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-4 p-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold">{module.name}</h1>
            <p className="text-sm text-muted-foreground">Standort-spezifische Einträge</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Standort-Filter */}
            {(isAdmin && locations.length > 1) && (
              <select
                value={selectedLocation}
                onChange={e => setSelectedLocation(e.target.value)}
                className="h-9 rounded-md border bg-background px-3 text-sm"
                data-testid="location-filter-select"
              >
                <option value="">Alle Standorte</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={handleExportCsv} data-testid="export-csv-btn">
                <FileDown className="h-4 w-4 mr-1" /> CSV
              </Button>
            )}
            {canAdd && (
              <Button size="sm" onClick={openAdd} data-testid="add-entry-btn">
                <Plus className="h-4 w-4 mr-1" /> Zeile hinzufügen
              </Button>
            )}
          </div>
        </div>

        {/* Tabelle */}
        <Card>
          <CardContent className="p-0">
            {entries.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground text-sm">
                Noch keine Einträge.{canAdd && ' Klicke "Zeile hinzufügen" um zu starten.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Datum</th>
                      {isAdmin && <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Standort</th>}
                      <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Erstellt von</th>
                      {schema.map(col => (
                        <th key={col.key} className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">
                          {col.label}{col.required && <span className="text-red-500 ml-0.5">*</span>}
                        </th>
                      ))}
                      <th className="w-10 px-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, idx) => (
                      <tr key={entry.id} className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/10'}`}>
                        <td className="px-4 py-2.5 text-muted-foreground text-xs">
                          {entry.created_at ? new Date(entry.created_at).toLocaleDateString('de-DE') : '–'}
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-2.5">
                            <Badge variant="outline" className="text-xs font-normal">
                              {locName(entry.location_id)}
                            </Badge>
                          </td>
                        )}
                        <td className="px-4 py-2.5 text-xs text-muted-foreground">
                          {entry.created_by_name || '–'}
                        </td>
                        {schema.map(col => (
                          <td key={col.key} className="px-4 py-2.5 font-medium">
                            {renderCell(col, entry.row_data?.[col.key])}
                          </td>
                        ))}
                        <td className="px-2 py-2.5">
                          {(isAdmin || (module.employee_can_delete_own && entry.created_by_id === user?.id)) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => handleDelete(entry.id)}
                              data-testid={`delete-entry-${entry.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Zeile hinzufügen Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Zeile hinzufügen – {module.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {/* Standort wählen (falls mehrere) */}
            {locations.length > 1 && isAdmin && (
              <div className="space-y-1">
                <label className="text-sm font-medium">Standort *</label>
                <select
                  value={selectedLocation}
                  onChange={e => setSelectedLocation(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">Bitte wählen...</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}

            {schema.map(col => (
              <div key={col.key} className="space-y-1">
                <label className="text-sm font-medium">
                  {col.label}{col.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {col.type === 'select' ? (
                  <select
                    value={rowData[col.key] || ''}
                    onChange={e => setRowData(d => ({ ...d, [col.key]: e.target.value }))}
                    className="h-9 w-full rounded-md border bg-background px-3 text-sm"
                    data-testid={`field-${col.key}`}
                  >
                    <option value="">Bitte wählen...</option>
                    {(col.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input
                    type={col.type === 'date' ? 'date' : col.type === 'number' ? 'number' : 'text'}
                    value={rowData[col.key] || ''}
                    onChange={e => setRowData(d => ({ ...d, [col.key]: e.target.value }))}
                    placeholder={col.label}
                    data-testid={`field-${col.key}`}
                  />
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Abbrechen</Button>
            <Button onClick={handleAddRow} disabled={saving} data-testid="confirm-add-entry-btn">
              {saving ? 'Speichern...' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
